import React, { useEffect, useMemo, useRef, useState } from "react";
import { Message } from "../chat/message";
import { DEFAULT_LABELS, RichTextEditor } from "@mantine/rte";
import { Button } from "@/dentlab/src/components/ui/button";
import { Loader2 } from "lucide-react";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { ProfilesForChat } from "@/lib/types/profile.types";
import { SquareArrowOutUpRight, X } from "lucide-react";
import { Logger } from "@/lib/logger/Logger";
import { Tables } from "@/lib/supabase/supabaseTypesImport";
import { showNotification } from "../../store/Central/selectors";
import { NewMessagesDivider } from "../chat/new-messages-divider.component";
import "../chat/styles.css";
import { JobRequestCustomV1 } from "@/lib/store/Realtime/message.types";
import { RolesEnum } from "@/lib/types/roles.types";
import { AcceptJobRequestResult } from "../../store/Jobs/job.store";

export interface FileInfo {
	name: string;
	type: string;
	size: number;
	data: string | ArrayBuffer | null;
}

const TAB_KEY_CODE = 9;

// Quill's default Tab binding inserts a tab character, which traps keyboard
// focus in the editor. Returning true lets the browser move focus instead.
const EDITOR_MODULES = {
	keyboard: {
		bindings: {
			tab: { key: TAB_KEY_CODE, handler: () => true },
		},
	},
};

const EDITOR_LABELS = {
	...DEFAULT_LABELS,
	bold: "Fett",
	italic: "Kursiv",
	underline: "Unterstrichen",
};

export const ChannelComponent: React.FC<{
	channel: Tables<"message_channels"> | null;
	organizationId: string | null;
	profileLookup: Record<string, ProfilesForChat>;
	messages: Tables<"messages_with_actions">[];
	messagesLoading?: boolean;
	onShareFile: (fileId: string) => void;
	onUnshareFile: (fileId: string) => void;
	onAcceptJobRequest?: (
		channelId: string | null,
		messageContent?: JobRequestCustomV1 | null
	) => Promise<AcceptJobRequestResult>;
	onDenyJobRequest?: (channelId: string | null) => Promise<boolean>;
	goTo?: () => void;
	updateJobFiles?: () => void;
	jobId?: number;
	vendorOrgId?: string;
	customerOrgId?: string;
}> = ({
	channel,
	organizationId,
	profileLookup,
	messages,
	messagesLoading = false,
	onShareFile,
	onUnshareFile,
	onAcceptJobRequest,
	onDenyJobRequest,
	goTo,
	updateJobFiles,
	jobId,
	vendorOrgId,
	customerOrgId,
}) => {
	const {
		sendMessage,
		role,
		userId,
		// Note that texts and files are stored per channel
		// If there is not channel, we fall back to the jobId
		getReplyText,
		setReplyText,
		setReplyFiles,
		getReplyFiles,
	} = useRealtimeStore((state) => ({
		role: state.role,
		sendMessage: state.sendMessage,
		userId: state.userId,
		getReplyText: state.getReplyText,
		setReplyText: state.setReplyText,
		setReplyFiles: state.setReplyFiles,
		getReplyFiles: state.getReplyFiles,
	}));

	const isAdmin =
		role !== undefined && role !== null && role <= RolesEnum.ADMIN;

	const [isUploading, setIsUploading] = useState(false);
	const [isSending, setIsSending] = useState(false);
	const isSendingRef = useRef(false);
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const calculatedDividerIdRef = useRef<string | null>(null);

	const initialMessageLoadCompleted = messages.length > 0;

	// Calculate the ID of the first message that should show the divider
	const dividerMessageId = useMemo(() => {
		// If we've already calculated for this channel, return the stored value
		if (calculatedDividerIdRef.current !== null) {
			return calculatedDividerIdRef.current;
		}

		// Only calculate if initial messages have loaded
		if (!initialMessageLoadCompleted || !channel?.last_read_at || !userId) {
			return "NO_UNREAD_MESSAGES";
		}

		// Find the first message that was created after last_read_at
		const firstUnreadMessage = messages.find(
			(message) =>
				message.created_at &&
				message.created_at > channel.last_read_at! &&
				message.created_by !== userId
		);

		// Store the calculated value
		// Use a sentinel value when there are no unread messages to prevent
		// the divider from appearing on new messages
		const dividerId = firstUnreadMessage?.id || "NO_UNREAD_MESSAGES";
		calculatedDividerIdRef.current = dividerId;

		return dividerId;
	}, [initialMessageLoadCompleted, channel?.last_read_at, messages, userId]);

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setIsUploading(true);
		if (!event.target.files || event.target.files.length === 0) {
			setIsUploading(false);
			return;
		}

		const fileList = Array.from(event.target.files);

		try {
			// Use Promise.all to wait for all file readings to complete
			const newFilesPromises = fileList.map((file) => {
				return new Promise<FileInfo>((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = (e) => {
						if (!e.target?.result) {
							reject(
								new Error(`Empty read result for ${file.name}`)
							);
							return;
						}
						resolve({
							name: file.name,
							type: file.type,
							size: file.size,
							data: e.target.result,
						});
					};
					reader.onerror = () => reject(reader.error);
					reader.readAsDataURL(file);
				});
			});

			// Wait for all files to be processed
			const newFiles = await Promise.all(newFilesPromises);

			// Update state once with all the new files
			const updatedFiles = [
				...getReplyFiles(channel?.id || null, jobId),
				...newFiles,
			];
			setReplyFiles(updatedFiles, channel?.id || null, jobId);
		} catch (error) {
			Logger.error(error, {}, "Error uploading files");
		} finally {
			setIsUploading(false);

			// Clear the file input so the same file can be selected again if needed
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const handleRemoveFile = (index: number) => {
		const updatedFiles = [...getReplyFiles(channel?.id || null, jobId)];
		updatedFiles.splice(index, 1);
		setReplyFiles(updatedFiles, channel?.id || null, jobId);
	};

	const messagesContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (messagesContainerRef.current) {
			messagesContainerRef.current.scrollTop =
				messagesContainerRef.current.scrollHeight;
		}
	}, [messages]);

	// Reset the calculated divider ID when channel changes
	useEffect(() => {
		calculatedDividerIdRef.current = null;
		isSendingRef.current = false;
		setIsSending(false);
	}, [channel?.id]);

	const handleSend = async () => {
		// Ref, not state: two clicks in the same tick both read a stale false.
		if (isSendingRef.current || isUploading) {
			return;
		}

		if (!channel && !(jobId && vendorOrgId && customerOrgId)) {
			showNotification({
				message:
					"Es kann keine Nachricht gesendet werden, da kein Kanal ausgewählt wurde.",
				type: "error",
			});
			return;
		}

		isSendingRef.current = true;
		setIsSending(true);
		try {
			// sendMessage handles its own errors (notification + false); the
			// catch below only covers unexpected throws.
			const sent = channel
				? await sendMessage()
				: // If no channel is selected, we are in a job page with still empty chat
					await sendMessage(true, jobId, vendorOrgId, customerOrgId);

			if (sent && updateJobFiles) {
				updateJobFiles();
			}
		} catch (error) {
			Logger.error(error, {}, "Error sending message");
			showNotification({
				message: "Nachricht konnte nicht gesendet werden.",
				type: "error",
			});
		} finally {
			isSendingRef.current = false;
			setIsSending(false);
		}
	};

	const replyFiles = getReplyFiles(channel?.id || null, jobId);

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			{goTo && channel?.job_id && (
				<button
					onClick={goTo}
					className="flex items-center justify-between w-full px-4 py-2.5 bg-primary/10 hover:bg-primary/15 border-b border-primary/15 transition-colors cursor-pointer group flex-shrink-0"
					data-testid="go-to-job-button"
					aria-label="Zum Auftrag navigieren"
				>
					<span className="flex items-center gap-2 text-sm truncate min-w-0">
						<span className="font-medium text-primary/80 truncate">
							{[channel.job_title, channel.job_code]
								.filter(Boolean)
								.join(" – ") || `Auftrag #${channel.job_id}`}
						</span>
						<span className="text-primary/30">|</span>
						<span className="flex items-center gap-1.5 font-medium text-primary whitespace-nowrap group-hover:gap-2.5 transition-all">
							Zum Auftrag
							<SquareArrowOutUpRight className="h-4 w-4" />
						</span>
					</span>
				</button>
			)}
			{!channel && (
				<div className="flex w-full min-h-0 flex-1 items-center justify-center">
					<div className="flex w-full h-full items-center justify-center">
						<div className="flex flex-col items-center justify-center mx-4 px-6 py-4 gap-4">
							<div className="mb-2 text-lg font-semibold text-gray-700">
								Chat starten
							</div>
							<div className="mb-4 text-sm font-semibold text-gray-500 text-center">
								Das Senden einer Nachricht oder Teilen einer
								Datei erstellt einen Chat für Labor und Kunde.{" "}
								<br />
								<br />
								Falls mit einem Auftrag verbunden, sind danach
								Titel und Status des Auftrags für den Kunden
								sichtbar.
							</div>
						</div>
					</div>
				</div>
			)}
			{channel && (
				<div
					className="flex flex-col flex-1 min-h-0 overflow-y-auto py-2 scroll-smooth bg-white"
					ref={messagesContainerRef}
				>
					{messagesLoading ? (
						<div className="flex w-full h-full items-center justify-center">
							<Loader2 className="h-8 w-8 animate-spin text-gray-400" />
						</div>
					) : messages && messages.length > 0 ? (
						(() => {
							return messages?.map((message) => {
								const handleAcceptJobRequest = async (
									messageContent?: JobRequestCustomV1 | null
								) => {
									if (!onAcceptJobRequest) {
										showNotification({
											message:
												"Fehler beim Akzeptieren der Anfrage",
											type: "error",
										});
										return false;
									}
									const result = await onAcceptJobRequest(
										channel.id ?? null,
										messageContent
									);
									if (result.success) {
										showNotification({
											message: result.wasAlreadyAccepted
												? "Auftrag wurde bereits angenommen"
												: "Auftrag erfolgreich angenommen",
											type: result.wasAlreadyAccepted
												? "info"
												: "success",
											action: {
												label: "Zum Auftrag",
												onClick: () => {
													window.location.href = `/client/${result.clientId}/job/${result.jobId}`;
												},
											},
										});
										return true;
									}
									return false;
								};
								const handleDenyJobRequest = async () => {
									if (!onDenyJobRequest) {
										showNotification({
											message:
												"Fehler beim Ablehnen der Anfrage",
											type: "error",
										});
										return false;
									}
									const success = await onDenyJobRequest(
										channel.id ?? null
									);
									if (success) {
										showNotification({
											message: "Anfrage abgelehnt",
											type: "success",
										});
										return true;
									}
									return false;
								};
								const user =
									profileLookup[message.created_by ?? ""];

								if (!user) {
									Logger.error(
										`User not found for message ${message.id}`,
										{},
										{ profileLookup, message }
									);
									return (
										<div>
											Nachricht {message.id} konnte nicht
											formatiert werden. Bitte
											kontaktieren Sie den Support.
										</div>
									);
								}

								// Show divider if this is the first unread message
								const shouldShowDivider =
									message.id === dividerMessageId;

								return (
									<React.Fragment key={message.id}>
										{shouldShowDivider && (
											<NewMessagesDivider />
										)}
										<Message
											message={message}
											currentOrganizationId={
												organizationId ?? ""
											}
											otherOrganizationId={
												user?.organization_id ?? ""
											}
											messageAt={message.created_at ?? ""}
											user={
												profileLookup[
													message.created_by ?? ""
												]
											}
											onShareFile={onShareFile}
											onUnshareFile={onUnshareFile}
											onAcceptJobRequest={
												handleAcceptJobRequest
											}
											onDenyJobRequest={
												handleDenyJobRequest
											}
											isAdmin={isAdmin}
										/>
									</React.Fragment>
								);
							});
						})()
					) : (
						<div className="flex w-full h-full items-center justify-center"></div>
					)}
				</div>
			)}
			<div className="chat-composer m-3 flex max-h-[50%] flex-shrink-0 flex-col overflow-hidden rounded-md border-2 border-gray-200 shadow-sm">
				{replyFiles.length > 0 && (
					<div className="flex max-h-[4.5rem] flex-shrink-0 flex-row flex-wrap gap-2 overflow-y-auto border-b border-gray-100 px-3 py-2">
						{replyFiles.map((file, index) => (
							<div
								key={index}
								className="flex h-8 max-w-[250px] items-center gap-1 rounded-md border px-2"
							>
								<span
									className="min-w-0 truncate text-xs"
									title={file.name}
								>
									{file.name}
								</span>
								<Button
									variant="ghost"
									size="icon"
									onClick={() => handleRemoveFile(index)}
									className="h-6 w-6 flex-shrink-0 p-0 hover:bg-gray-100"
									aria-label={`Datei ${file.name} entfernen`}
									data-testid={`chat-remove-attachment-button-${index}`}
								>
									<X className="text-gray-500 h-3.5 w-3.5" />
								</Button>
							</div>
						))}
					</div>
				)}
				<RichTextEditor
					className="chat-composer-editor"
					style={{ border: "none" }}
					controls={[["italic", "underline", "bold"]]}
					labels={EDITOR_LABELS}
					modules={EDITOR_MODULES}
					// Until a channel resolves the store drops every keystroke.
					readOnly={
						!channel && !(jobId && vendorOrgId && customerOrgId)
					}
					value={getReplyText(channel?.id || null, jobId)}
					onChange={(e) =>
						setReplyText(e, channel?.id || null, jobId)
					}
					onKeyDown={(e) => {
						if (
							e.key.toLocaleLowerCase() === "enter" &&
							(e.metaKey || e.ctrlKey)
						) {
							e.preventDefault();
							handleSend();
						}
					}}
				/>
				<div className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-gray-100 px-3 py-2">
					<input
						type="file"
						ref={fileInputRef}
						onChange={handleFileChange}
						className="hidden"
						multiple
						disabled={isUploading}
						aria-label="Dateien auswählen"
						data-testid="chat-file-input"
					/>
					<Button
						onClick={() => {
							fileInputRef.current?.click();
						}}
						size="sm"
						disabled={isUploading}
						variant="outline"
						className="min-w-[140px]"
						aria-busy={isUploading}
						data-testid="chat-upload-file-button"
					>
						{isUploading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Dateien hochladen
							</>
						) : (
							"Datei hochladen"
						)}
					</Button>
					<Button
						onClick={handleSend}
						size="sm"
						disabled={isSending || isUploading}
						aria-busy={isSending}
						data-testid="chat-send-button"
					>
						{isSending ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Senden
							</>
						) : (
							"Senden"
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};
